import { Injectable } from '@angular/core';
import { Roi, RoiSize, RoiSizeService } from '@analysis/domain';

export interface RoiSizeTile {
  labelKey: string;
  main: string;
  secondary?: string;
}
import { RoiStore, RoiChangeStore, AssetStore, MosaicGalleryPanels } from '../../stores';
import { ProtocolStore } from '@telespot/protocols/ui';
import { RoiRevertEffects } from '../../effects';
import { OverlayData } from '@shared/ui';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import type { TileSourceOptions } from 'openseadragon';

@Injectable()
export class CropDetailFacade {
  public readonly focusedRoiId$ = this.roiStore.focusedRoiId$;
  public readonly tileSource$ = this.assetStore.tileSource$;
  public readonly hspan$ = this.assetStore.hspan$;
  public readonly rois$ = this.roiStore.rois$;
  public readonly changes$ = this.roiChangeStore.changedRois$;
  public readonly options$ = this.protocolStore.options$;
  public readonly selectedOverlayIds$ = this.panelRegistry.focusedPanelSelectedItems$;

  public readonly focusedRoiSize$ = this.roiStore.select(
    this.roiStore.focusedRoiId$,
    this.roiStore.rois$,
    this.assetStore.tileSource$,
    this.assetStore.hspan$,
    (focusedRoiId, rois, tileSource, hspan): RoiSize | null => {
      if (!focusedRoiId || !tileSource) return null;

      const roi = rois[focusedRoiId];
      if (!roi) return null;

      return RoiSizeService.computeRoiSize(
        roi.coordinates,
        { width: tileSource.width, height: tileSource.height },
        hspan
      );
    }
  );

  public readonly focusedRoiSizeTiles$ = this.roiStore.select(this.focusedRoiSize$, (size): RoiSizeTile[] =>
    size ? this.buildSizeTiles(size) : []
  );

  // TODO: Move this to a feature
  public readonly overlayRoiIds$ = this.roiStore.select(
    this.roiStore.assetIndex$,
    this.assetStore.selectedAssetId$,
    (assetIndex, selectedAssetId) => assetIndex.get(selectedAssetId) ?? new Set<string>()
  );

  // TODO: Move this to a feature
  public readonly roiOverlays$ = this.roiStore.select(
    this.overlayRoiIds$,
    this.rois$,
    this.tileSource$,
    this.focusedRoiId$,
    this.selectedOverlayIds$,
    this.options$,
    (overlayRoiIds, rois, tileSource, focusedRoiId, selectedOverlayIds, options): OverlayData[] => {
      if (!tileSource || !overlayRoiIds?.size) return [];

      return Array.from(overlayRoiIds)
        .filter((id) => rois[id])
        .map((id) => {
          const roi = rois[id];
          return {
            x: roi.coordinates.x0 * tileSource.width,
            y: roi.coordinates.y0 * tileSource.height,
            w: (roi.coordinates.x1 - roi.coordinates.x0) * tileSource.width,
            h: (roi.coordinates.y1 - roi.coordinates.y0) * tileSource.height,
            id: roi.id,
            color: this.getRoiColor(id, focusedRoiId, selectedOverlayIds),
            tooltip: this.getRoiTooltip(id, rois, options),
            selected: false,
          };
        });
    }
  );

  public centerPoint: { x: number; y: number } = { x: 0.5, y: 0.5 };
  public zoomLevel = 4;
  public multiselectionEnabled = false;
  public dragSelectionEnabled = false;

  get viewerMode(): string {
    return this.multiselectionEnabled || this.dragSelectionEnabled ? 'selecting' : 'idle';
  }

  constructor(
    private readonly roiStore: RoiStore,
    private readonly roiChangeStore: RoiChangeStore,
    private readonly assetStore: AssetStore,
    private readonly protocolStore: ProtocolStore,
    private readonly roiRevertEffects: RoiRevertEffects,
    private readonly panelRegistry: MosaicGalleryPanels
  ) {
    this.tileSource$.pipe(takeUntil(this.roiStore.destroy$), distinctUntilChanged()).subscribe(() => {
      this.resetZoom();
    });

    combineLatest([this.focusedRoiId$, this.tileSource$, this.rois$])
      .pipe(takeUntil(this.roiStore.destroy$))
      .subscribe(([focusedRoiId, tileSource, rois]) => this.updateCenterPoint(focusedRoiId, tileSource, rois));

    combineLatest([this.focusedRoiId$, this.selectedOverlayIds$])
      .pipe(takeUntil(this.roiStore.destroy$))
      .subscribe(([focusedRoiId, selectedOverlayIds]) => {
        const ids = selectedOverlayIds instanceof Set ? selectedOverlayIds : new Set<string>();
        if (!focusedRoiId && ids.size) {
          this.setFocusedRoi(Array.from(ids).at(0));
        }
      });
  }

  public resetZoom(): void {
    this.zoomLevel = 4;
  }

  public revertRoiChanges(roiId: string): void {
    this.roiRevertEffects.revertRoiChanges(roiId);
  }

  public filterByRoiIds(roiIds: string[]): void {
    this.panelRegistry.filterFocusedPanelByRoiIds(roiIds);
  }

  public setFocusedRoi(roiId: string): void {
    this.roiStore.setFocusedRoi(roiId);
  }

  private updateCenterPoint(focusedRoiId: string, tileSource: TileSourceOptions, rois: Record<string, Roi>): void {
    if (!tileSource || !focusedRoiId || !rois[focusedRoiId]) return;

    const roi = rois[focusedRoiId];
    this.centerPoint = {
      x: ((roi.coordinates.x0 + roi.coordinates.x1) * tileSource.width) / 2,
      y: ((roi.coordinates.y0 + roi.coordinates.y1) * tileSource.height) / 2,
    };
  }

  private buildSizeTiles(size: RoiSize): RoiSizeTile[] {
    const dimensions = [
      { labelKey: 'analysis.roi_size_width', px: size.widthPx, um: size.widthUm },
      { labelKey: 'analysis.roi_size_height', px: size.heightPx, um: size.heightUm },
      { labelKey: 'analysis.roi_size_area', px: size.areaPx, um: size.areaUm2, unitSuffix: '²' },
    ];

    // Zero-sized dimensions carry no information (e.g. pois, whose coordinates are degenerate)
    return dimensions
      .filter(({ px }) => px !== 0)
      .map(({ labelKey, px, um, unitSuffix }) => this.buildSizeTile(labelKey, px, um, unitSuffix));
  }

  private buildSizeTile(labelKey: string, px: number, um?: number, unitSuffix = ''): RoiSizeTile {
    const pxText = `${Math.round(px)} px${unitSuffix}`;
    if (um === undefined) return { labelKey, main: pxText };
    return { labelKey, main: `${um.toFixed(1)} µm${unitSuffix}`, secondary: pxText };
  }

  private getRoiColor(id: string, focusedRoiId: string, selectedOverlayIds: Set<string>): string {
    if (focusedRoiId === id) return 'rgba(255, 0, 0, 1)';
    if (selectedOverlayIds.has(id)) return 'rgba(0, 255, 255, 1)';
    return 'rgba(0, 0, 0, 1)';
  }

  private getRoiTooltip(
    id: string,
    rois: Record<string, { labels: Map<string, number> }>,
    options: Record<string, { name: string }>
  ): string {
    if (rois[id].labels.size === 0) return '(no tags)';

    const labelArray = Array.from(rois[id].labels.keys());
    const labelNames = labelArray.map((label) => options[label]?.name ?? label ?? '???');

    return labelNames.join(', ');
  }
}
